import React, { Fragment, useLayoutEffect } from "react"
import { useEditor, EditorContent } from "@tiptap/react"
import StarterKit from "@tiptap/starter-kit"
import TextStyle from "@tiptap/extension-text-style"
import Color from "@tiptap/extension-color"
import Highlight from "@tiptap/extension-highlight"
import Table from "@tiptap/extension-table"
import TableRow from "@tiptap/extension-table-row"
import TableCell from "@tiptap/extension-table-cell"
import TableHeader from "@tiptap/extension-table-header"
import Link from "@tiptap/extension-link"
import FontFamily from "@tiptap/extension-font-family"
import TextAlign from "@tiptap/extension-text-align"
import FontSize from "tiptap-extension-font-size"
import ResizeImage from "tiptap-extension-resize-image"
import Placeholder from "@tiptap/extension-placeholder"
import TaskItem from "@tiptap/extension-task-item"
import TaskList from "@tiptap/extension-task-list"
import clsx from "clsx"

import EditorTextDecoration from "./EditorTextDecoration"
import EditorHeading from "./EditorHeading"
import EditorFontSize from "./EditorFontSize"
import EditorAlignment from "./EditorAlignment"
import EditorColorPicker from "./EditorColorPicker"
import EditorAddLink from "./EditorAddLink"
import EditorUploader from "./EditorUploader"
import EditorTable from "./EditorTable"
import EditorLists from "./EditorLists"
import classes from "./index.module.scss"

const Editor = (props) => {
  const {
    error,
    className,
    headerClassName,
    editorClassName,
    placeholder,
    imageCategory,
    initialValue = "",
    onChange = () => { },
    tools = {
      textStyle: true,
      headers: true,
      fontSize: true,
      lists: true,
      alignment: true,
      colorPicker: true,
      link: true,
      upload: true,
      table: true
    }
  } = props

  const editor =
    useEditor({
      onCreate: ({ editor }) => editor.commands.setContent(initialValue),
      onUpdate: ({ editor }) => onChange(editor.getHTML()),
      extensions: [
        StarterKit,
        TextStyle,
        FontFamily,
        FontSize,
        Color,
        Highlight.configure({ multicolor: true }),
        Table.configure({ resizable: true }),
        TableRow,
        TableHeader,
        TableCell,
        TaskList,
        TaskItem,
        Placeholder.configure({ placeholder }),
        ResizeImage.configure({
          resizeIcon: true,
          displayWidth: true
        }),
        Link.configure({ openOnClick: false }),
        TextAlign.configure({
          types: ["heading", "paragraph"],
          alignments: ["left", "center", "right", "justify"],
          defaultAlignment: "right",
        })
      ]
    })

  useLayoutEffect(() => {
    editor.commands.setContent(initialValue || "")
  }, [initialValue])

  return (
    <Fragment>
      <div className={className}>
        <div className={clsx(classes["menu"], headerClassName)}>
          {
            tools.textStyle &&
            <EditorTextDecoration
              editor={editor}
            />
          }

          {
            tools.headers &&
            <EditorHeading
              editor={editor}
            />
          }

          {
            tools.fontSize &&
            <EditorFontSize
              editor={editor}
            />
          }

          {
            tools.lists &&
            <EditorLists
              editor={editor}
            />
          }

          {
            tools.alignments &&
            <EditorAlignment
              editor={editor}
            />
          }

          {
            tools.colorPicker &&
            <EditorColorPicker
              editor={editor}
            />
          }

          {
            tools.link &&
            <EditorAddLink
              editor={editor}
            />
          }

          {
            tools.upload &&
            <EditorUploader
              editor={editor}
              imageCategory={imageCategory}
            />
          }

          {
            tools.table &&
            <EditorTable
              editor={editor}
            />
          }
        </div>

        <EditorContent
          id="editor"
          className={clsx(classes["editor"], editorClassName)}
          editor={editor}
        />
      </div>

      {
        !!error &&
        <div className={classes["error"]}>
          {error}
        </div>
      }
    </Fragment>
  )
}

export default Editor